import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../translations/translations';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { language, changeLanguage } = useLanguage();

  const navItems = [
    { label: t(language, 'nav.howItWorks'), path: '/how-it-works' },
    { label: t(language, 'nav.services'), path: '/services' },
    { label: t(language, 'nav.fit'), path: '/fit' },
    { label: t(language, 'nav.insights'), path: '/insights' },
    { label: t(language, 'nav.faq'), path: '/faq' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="sticky top-0 z-50 bg-pearl/95 backdrop-blur-sm border-b border-midnight/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="nav-logo">
            <img
              src="/brand/sin-fondo-1.png"
              alt="BIMCORP MX"
              className="h-8 sm:h-9 w-auto"
              loading="eager"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`text-sm font-medium transition-colors hover:text-blue-900 ${
                  isActive(item.path) ? 'text-blue-900' : 'text-gray-700'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>

          {/* Language Selector & CTA */}
          <div className="hidden md:flex items-center space-x-4">
            {/* Language Switcher */}
            <div className="flex items-center space-x-1 bg-gray-100 rounded-md p-1">
              <button
                onClick={() => changeLanguage('en')}
                className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                  language === 'en'
                    ? 'bg-white text-blue-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                EN
              </button>
              <button
                onClick={() => changeLanguage('es')}
                className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                  language === 'es'
                    ? 'bg-white text-blue-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                ES
              </button>
            </div>

            <Link to="/start">
              <Button className="shine-cta bg-blue-900 hover:bg-blue-800 text-white">
                {t(language, 'nav.startCta')}
              </Button>
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-100 transition-colors"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div className="md:hidden border-t border-gray-200 bg-white">
          <div className="px-4 py-4 space-y-3">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsOpen(false)}
                className={`block px-3 py-2 rounded-md text-base font-medium transition-colors ${
                  isActive(item.path)
                    ? 'bg-blue-50 text-blue-900'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                {item.label}
              </Link>
            ))}
            
            {/* Mobile Language Switcher */}
            <div className="flex items-center justify-center space-x-2 py-2">
              <Globe size={16} className="text-gray-600" />
              <div className="flex items-center space-x-1 bg-gray-100 rounded-md p-1">
                <button
                  onClick={() => changeLanguage('en')}
                  className={`px-3 py-1 text-sm font-medium rounded transition-colors ${
                    language === 'en'
                      ? 'bg-white text-blue-900 shadow-sm'
                      : 'text-gray-600'
                  }`}
                >
                  EN
                </button>
                <button
                  onClick={() => changeLanguage('es')}
                  className={`px-3 py-1 text-sm font-medium rounded transition-colors ${
                    language === 'es'
                      ? 'bg-white text-blue-900 shadow-sm'
                      : 'text-gray-600'
                  }`}
                >
                  ES
                </button>
              </div>
            </div>

            <Link to="/start" onClick={() => setIsOpen(false)}>
              <Button className="shine-cta w-full bg-blue-900 hover:bg-blue-800 text-white">
                {t(language, 'nav.startCta')}
              </Button>
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
